import { RiBarChartLine } from '@remixicon/react';

import {
  ChartBlock,
  extractChartSpec,
} from '@spotify/confidence-conversation-react';
import { useFeatureFlag } from '@spotify/confidence-core-react';
import {
  type ToolGroupEntry,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';
import {
  PULSE_BLOCK_TYPE,
  SaveToPulseButton,
} from '@spotify/plugin-confidence-pulse-react';

import { confidenceChatPlugin } from '../plugin';
import classes from './CreateChartToolExtension.module.css';

const getSourceChatName = (eventName?: string): string | undefined => {
  const eventPathIndex = eventName?.indexOf('/events/') ?? -1;
  return eventPathIndex >= 0 ? eventName?.slice(0, eventPathIndex) : undefined;
};

const getChartTitle = (spec: string): string | undefined => {
  try {
    const parsed = JSON.parse(spec) as { title?: unknown };
    const title = parsed.title;

    if (typeof title === 'string') {
      return title.trim() || undefined;
    }

    if (title && typeof title === 'object') {
      const text = (title as { text?: unknown }).text;
      return typeof text === 'string' ? text.trim() || undefined : undefined;
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const getCaptureDisplayName = (spec: string): string => {
  return getChartTitle(spec) ?? 'Chart';
};

const CreateChartToolRenderer = ({ entry }: { entry: ToolGroupEntry }) => {
  const { enabled: pulsesEnabled } = useFeatureFlag('pulses', {
    enabled: false,
  });
  const spec = extractChartSpec({
    content: entry.result?.content ?? '',
    metadata: { toolInput: entry.input },
  });
  if (!spec) return null;

  const sourceChat = getSourceChatName(entry.eventName);
  const canCapture = pulsesEnabled && !!sourceChat;

  return (
    <div className={classes.chartWithActions}>
      {canCapture && (
        <div className={classes.actions}>
          <SaveToPulseButton
            input={{
              sourceChat,
              sourceEvent: entry.eventName,
              suggestedName: getCaptureDisplayName(spec),
              blockType: PULSE_BLOCK_TYPE.CHART,
              visualizationSpec: spec,
            }}
          />
        </div>
      )}
      <ChartBlock content={spec} />
    </div>
  );
};

export const CreateChartToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'create-chart',
    predicate: tool => tool.endsWith('create_chart'),
    icon: RiBarChartLine,
    display: 'prominent',
    render: () => null,
    renderTool: entry => <CreateChartToolRenderer entry={entry} />,
  }),
);
